/* FOLLOWER BOOST — Hero V5.2 scene methods (layout solve, element state, trails), mixed into FbHeroV52. */
// helpers come from fb-hero-v52-lib.js, loaded first by fb-hero-v52.js (versioned URL)
const { clamp, lerp, smooth, easeOut3, easeInOut, span, bez, prng, screenAt, solve, NET_GLOW } = globalThis.__fbV52lib;

/* Second plane: put each service on the free grid point closest to its seed, clear of the logos
   (fixed circles), of the services already placed, of the phone and inside the box. */
function placeGaps(items, fixed, box, excl) {
  const placed = [];
  const step = Math.max(6, items[0] ? items[0].r / 2 : 8);
  for (const it of items) {
    let best = null;
    let bd = Infinity;
    for (let y = box.T + it.r; y <= box.B - it.r; y += step) {
      for (let x = box.L + it.r; x <= box.R - it.r; x += step) {
        const d = Math.hypot(x - it.x, y - it.y);
        if (d >= bd) continue;
        let ok = true;
        for (const e of excl) if (x > e.l - it.r * 0.3 && x < e.r + it.r * 0.3 && y > e.t - it.r * 0.3) { ok = false; break; }
        if (!ok) continue;
        for (const o of fixed) if (Math.hypot(x - o.x, y - o.y) < o.r + it.r + it.gap) { ok = false; break; }
        if (!ok) continue;
        for (const o of placed) if (Math.hypot(x - o.x, y - o.y) < o.r + it.r + it.gap) { ok = false; break; }
        if (ok) { bd = d; best = [x, y]; }
      }
    }
    if (best) { it.x = best[0]; it.y = best[1]; }
    placed.push(it);
  }
  return items;
}

export const sceneMethods = {
  /* cover-fit + composition solved for this viewport */
  layout() {
    this.measureHeader();
    const w = this.pin.clientWidth;
    const h = this.pin.clientHeight;
    if (!w || !h) return;
    const desk = this.mode === 'd';
    const dpr = Math.min(window.devicePixelRatio || 1, desk ? 1.5 : 2);
    for (const c of [this.canvas, this.tc]) { c.width = Math.round(w * dpr); c.height = Math.round(h * dpr); }
    this.dpr = dpr;
    this.W = w;
    this.H = h;
    const fw = desk ? 16 : 9;
    const fh = desk ? 9 : 16;
    const s = Math.max(w / fw, h / fh);
    const cw = fw * s;
    const ch = fh * s;
    this.cover = { x: clamp(w / 2 - this.focus[0] * cw, w - cw, 0), y: clamp(h / 2 - this.focus[1] * ch, h - ch, 0), w: cw, h: ch };
    const C = this.cover;
    const ph = { l: C.x + this.phone.l * C.w, r: C.x + this.phone.r * C.w, t: C.y + this.phone.t * C.h, b: C.y + this.phone.b * C.h };
    const [sx0, sy0] = screenAt(this.screens, 0.5);
    const S = { x: C.x + sx0 * C.w, y: C.y + sy0 * C.h };
    this.L = desk ? clamp(w * 0.064, 72, 120) : clamp(w * 0.17, 54, 76); // logo size
    this.SV = Math.round(this.L * (desk ? 0.56 : 0.6)); // service orb size
    this.el.style.setProperty('--fb-v52-logo', `${Math.round(this.L)}px`);
    this.el.style.setProperty('--fb-v52-svc', `${this.SV}px`);
    let box;
    const excl = [{ ...ph, k: 0.35, sides: [0, 1, 2] }];
    if (desk) {
      const tr = this.copy.querySelector('.fb-hero__title')?.getBoundingClientRect();
      const pr = this.pin.getBoundingClientRect();
      box = { L: tr ? tr.right - pr.left + 28 : w * 0.44, R: w - 14, T: 14, B: ph.b };
    } else {
      this.actH = this.actions ? this.actions.offsetHeight : 44;
      this.actTop = this.actions ? this.actions.offsetTop + this.copy.offsetTop : 0;
      box = { L: 8, R: w - 8, T: 12 + this.actH + 18, B: ph.b };
      this.copyB = this.copy.offsetTop + this.copy.offsetHeight;
      this.el.style.setProperty('--fb-v52-copy-b', `${Math.round(this.copyB)}px`);
    }
    this.box = box;
    this.S = S;
    const L = this.L;
    const SV = this.SV;
    const logos = this.logos;
    const n = logos.length;
    const top = box.T;
    const ry = Math.max(L * 1.6, ph.t - top - L * 0.2);
    const rxL = S.x - box.L;
    const rxR = box.R - S.x;
    // constellation (S3–S4): logos on two arcs above the phone; services between them
    const cons = logos.map((f, k) => {
      const a = Math.PI * (1.04 + (k + 0.5) / n * 0.92); // ~187° … ~353°
      const ring = k % 2 ? 0.58 : 0.92;
      const cx = Math.cos(a);
      return { f, x: S.x + cx * (cx < 0 ? rxL : rxR) * ring * 0.95, y: S.y + Math.sin(a) * ry * ring, r: L * 0.5, gap: L * 0.34, w: 1 };
    });
    const svcC = this.services.map((f, k) => {
      const a = Math.PI * (1.08 + (k + 0.5) / Math.max(1, this.services.length) * 0.84);
      const cx = Math.cos(a);
      return { f, x: S.x + cx * (cx < 0 ? rxL : rxR) * 0.76, y: S.y + Math.sin(a) * ry * 0.76, r: SV * 0.5, gap: SV * 0.45, w: 0.01 };
    });
    // logos first (first plane), then services solved in the gaps around the fixed logos (second plane)
    solve(cons, box, excl).forEach((nd) => { nd.f.cx = nd.x; nd.f.cy = nd.y; });
    placeGaps(svcC, cons, box, excl).forEach((nd) => { nd.f.cx = nd.x; nd.f.cy = nd.y; });
    // explosion (S5): pushed outward toward the viewer, bigger, may cross the frame edge
    const exp = logos.map((f) => {
      const dx = f.cx - S.x;
      const dy = f.cy - S.y;
      const sc = f.plane === 2 ? 1.5 : f.plane === 1 ? 1.15 : 0.82;
      f.es = sc;
      return { f, x: S.x + dx * 1.3, y: S.y + dy * 1.22, r: L * 0.5 * sc, gap: L * 0.3, w: 1, out: f.plane === 2 ? L * 0.35 : 0 };
    });
    const svcE = this.services.map((f) => ({ f, x: S.x + (f.cx - S.x) * 1.12, y: S.y + (f.cy - S.y) * 1.1, r: SV * 0.5 * 0.85, gap: SV * 0.4, w: 0.01 }));
    // top edge: keep the oversized front-plane logos fully under the header
    const ebox = { L: desk ? box.L - L * 0.2 : box.L, R: box.R, T: desk ? 10 + L * 0.35 : box.T - 6 + L * 0.35, B: box.B };
    solve(exp, ebox, excl).forEach((nd) => { nd.f.ex = nd.x; nd.f.ey = nd.y; });
    placeGaps(svcE, exp, ebox, excl).forEach((nd) => { nd.f.ex = nd.x; nd.f.ey = nd.y; });
    // conclusion: networks line up at the bottom like the catalogue grid that follows
    // V10 : sur ordinateur, la grille finale reste dans la colonne de la scène (sous le téléphone), sur deux
    // rangées : elle ne recouvre plus les avantages (« Sécurisé & fiable », « Support par e-mail ») à gauche.
    const colL = box.L;
    const colR = box.R;
    const perD = Math.ceil(n / 2);
    const rowSize = desk ? Math.min(L * 0.7, (colR - colL) / perD - 16) : Math.min(L * 0.62, (w - 24) / Math.ceil(n / 2) - 10);
    this.rowSize = rowSize;
    logos.forEach((f, k) => {
      if (desk) {
        const row = k < perD ? 0 : 1;
        const idx = row ? k - perD : k;
        const cnt = row ? n - perD : perD;
        const gapx = Math.min(rowSize * 0.75, (colR - colL - cnt * rowSize) / Math.max(1, cnt - 1));
        const rowW = cnt * rowSize + (cnt - 1) * gapx;
        const x0 = clamp(S.x - rowW / 2, colL, colR - rowW);
        f.rx = x0 + rowSize / 2 + idx * (rowSize + gapx);
        f.ry = h - 18 - rowSize * 0.5 - (1 - row) * (rowSize + 14);
      } else {
        const per = Math.ceil(n / 2);
        const row = k < per ? 0 : 1;
        const idx = row ? k - per : k;
        const cnt = row ? n - per : per;
        const gapx = (w - 24 - cnt * rowSize) / Math.max(1, cnt - 1);
        f.rx = 12 + rowSize / 2 + idx * (rowSize + gapx);
        f.ry = h - 16 - rowSize * 0.5 - (1 - row) * (rowSize + 12);
      }
    });
    // intro (S1): wave 0 already in the air, close to the phone
    logos.filter((f) => f.wave === 0).forEach((f, k) => {
      const a = Math.PI * [1.12, 1.88, 1.36][k % 3];
      const rr = k === 2 ? 1.35 : 1;
      f.ix = S.x + Math.cos(a) * (desk ? L * 2.6 : L * 1.9) * rr;
      f.iy = Math.min(S.y + Math.sin(a) * (desk ? L * 1.7 : L * 1.9) * rr - L * 0.3, ph.t - L * 0.55);
      // mobile: the copy is still readable at p = 0, keep the first wave below it
      if (!desk) f.iy = Math.max(f.iy, this.copyB + L * 0.6);
    });
    // wave 1 is already parked on its slots while wave 0 still waits on its intro spots (p 0.25–0.3):
    // keep the intro spots clear of those slots
    const parked = logos.filter((f) => f.wave === 1).map((f) => ({ f, x: f.cx, y: f.cy, sx: f.cx, sy: f.cy, r: L * 0.5, gap: L * 0.3, w: 100 }));
    const intro = logos.filter((f) => f.wave === 0).map((f) => ({ f, x: f.ix, y: f.iy, r: L * 0.47, gap: L * 0.3, w: 1 }));
    solve([...parked, ...intro], box, [], 140).forEach((nd) => {
      if (nd.f.wave !== 0) return;
      nd.f.ix = nd.x;
      nd.f.iy = desk ? nd.y : Math.max(nd.y, this.copyB + L * 0.6);
    });
    // V8 signature (last second): centred over the phone on desktop, between the CTAs and the phone on mobile
    this.sigPos = desk ? { x: S.x, y: Math.max(box.T + L * 0.9, ph.t - L * 1.25) } : { x: w / 2, y: (box.T + ph.t) / 2 };
    // V10 : écran vivant + orbite finale autour du téléphone + signature replacée (fb-hero-v10-screen.js)
    if (this.V10) this.V10.layout(this, { box, ph, S, w, h, desk });
    this.el.style.setProperty('--sig-x', `${Math.round(this.sigPos.x)}px`);
    this.el.style.setProperty('--sig-y', `${Math.round(this.sigPos.y)}px`);
    this.drawn = false;
  },

  /* position / scale / opacity of an element at progress p (pure) */
  state(f, p, S) {
    const L = this.L;
    const prep = smooth(span(p, 0.7, 0.8));
    const conc = easeInOut(span(p, 0.95, 1));
    let x;
    let y;
    let sc;
    let op;
    let rot = 0;
    let ry = 0;
    let blur = 0;
    if (f.kind === 'net') {
      // emergence along a curved path from the screen
      const em = easeOut3(span(p, f.e0, f.e1));
      const tx = f.wave === 0 ? f.ix : f.cx;
      const ty = f.wave === 0 ? f.iy : f.cy;
      const cx = S.x + (tx - S.x) * 0.15 + f.side * L * 0.9;
      const cy = Math.min(S.y, ty) - L * 0.8;
      x = bez(S.x, cx, tx, em);
      y = bez(S.y, cy, ty, em);
      sc = lerp(0.18, f.wave === 0 ? 0.9 : 1, em);
      op = smooth(span(em, 0, 0.25));
      ry = (1 - em) * 160 * f.side;
      // wave 0 rises from its intro spot to its constellation slot
      if (f.wave === 0) {
        const up = easeInOut(span(p, 0.3, 0.46));
        x = lerp(x, f.cx, up);
        y = lerp(y, f.cy, up);
        sc = lerp(sc, 1, up);
      }
      // gentle drift so the constellation breathes with the scroll
      const dr = Math.sin(p * 14 + f.i * 1.7) * L * 0.06;
      y += dr;
      // preparation: the constellation tightens toward the screen
      x = lerp(x, lerp(x, S.x, 0.16), prep);
      y = lerp(y, lerp(y, S.y, 0.12), prep);
      sc *= 1 - 0.08 * prep;
      // explosion toward the viewer
      const ex = easeOut3(span(p, 0.8 + f.dl, 0.9 + f.dl));
      if (ex > 0) {
        x = lerp(x, f.ex, ex);
        y = lerp(y, f.ey, ex);
        sc = lerp(sc, f.es, ex);
        rot = Math.sin(ex * Math.PI) * f.spin * 0.4;
        blur = f.plane === 0 ? 1.2 * ex : 0;
        op = 1;
      }
      const hold = span(p, 0.92, 0.95);
      x += (x - S.x) * 0.025 * hold;
      y += (y - S.y) * 0.025 * hold;
      // conclusion: line up at the bottom
      if (conc > 0) {
        x = lerp(x, f.rx, conc);
        y = lerp(y, f.ry, conc);
        sc = lerp(sc, f.rs ?? this.rowSize / L, conc); // V10 : taille selon la profondeur de l'orbite
        op = lerp(op, f.ro ?? 1, conc);
        rot *= 1 - conc;
        blur *= 1 - conc;
      }
    } else {
      const em = easeOut3(span(p, f.e0, f.e1));
      const cx = S.x + f.side * L * 0.6;
      const cy = Math.min(S.y, f.cy) - L * 0.4;
      x = bez(S.x, cx, f.cx, em);
      y = bez(S.y, cy, f.cy, em);
      sc = lerp(0.3, 1, em);
      op = smooth(span(em, 0, 0.3));
      y += Math.sin(p * 16 + f.i * 2.1) * L * 0.05;
      x = lerp(x, lerp(x, S.x, 0.2), prep);
      y = lerp(y, lerp(y, S.y, 0.16), prep);
      const ex = easeOut3(span(p, 0.8 + f.dl, 0.88 + f.dl));
      if (ex > 0) {
        x = lerp(x, f.ex, ex);
        y = lerp(y, f.ey, ex);
        sc = lerp(sc, 0.85, ex);
        op *= 1 - 0.15 * ex;
        blur = 0.6 * ex;
      }
      // badges disperse first in the conclusion
      const out = smooth(span(p, 0.9, 0.935));
      if (out > 0) {
        x += (x - S.x) * 0.35 * out;
        y += (y - S.y) * 0.35 * out - L * 0.4 * out;
        op *= 1 - out;
        sc *= 1 - 0.3 * out;
      }
    }
    return { x, y, sc, op, rot, ry, blur };
  },

  labelOn(f, p) {
    const inArrival = p >= f.lblA && p <= f.lblB;
    const atClimax = f.hero && p >= 0.85 && p < 0.9;
    return inArrival || atClimax;
  },

  /* side of a service label (1 right, -1 left, 0 hidden) so it never covers a logo, another label
     or the frame edge; this._lbls collects the labels already placed in this frame */
  labelSide(f, s) {
    if (f.lw === undefined) f.lw = f.n.querySelector('.fb-v52__lbl')?.offsetWidth || 90;
    const half = (this.SV * s.sc) / 2 + 6;
    const hh = 12;
    const top = this.mode === 'd' ? 0 : this.box.T - 10;
    const free = (l, r) => {
      if (l < (this.mode === 'd' ? this.box.L - 12 : 4) || r > this.W - 4 || s.y - hh < top) return false;
      for (const g of this.logos) {
        if (!g.s || g.s.op < 0.3) continue;
        const rad = (this.L * g.s.sc) / 2;
        const cx = clamp(g.s.x, l, r);
        const cy = clamp(g.s.y, s.y - hh, s.y + hh);
        if (Math.hypot(g.s.x - cx, g.s.y - cy) < rad) return false;
      }
      for (const b of this._lbls) if (l < b.r && r > b.l && s.y - hh < b.b && s.y + hh > b.t) return false;
      return true;
    };
    const R = [s.x + half, s.x + half + f.lw];
    const Lr = [s.x - half - f.lw, s.x - half];
    const prefer = s.x > this.S.x ? [R, Lr] : [Lr, R];
    for (const [l, r] of prefer) {
      if (free(l, r)) { this._lbls.push({ l, r, t: s.y - hh, b: s.y + hh }); return l < s.x ? -1 : 1; }
    }
    return 0;
  },

  /* light trails behind elements in flight + sparks around the screen (third plane) */
  drawTrails(p, S) {
    const g = this.tctx;
    const k = this.dpr;
    g.setTransform(k, 0, 0, k, 0, 0);
    g.clearRect(0, 0, this.W, this.H);
    if (this.static) return;
    g.globalCompositeOperation = 'lighter';
    g.lineCap = 'round';
    for (const f of [...this.logos, ...this.services]) {
      const inEm = p > f.e0 && p < f.e1 + 0.02;
      const inEx = p > 0.8 + f.dl && p < 0.92 + f.dl;
      if (!inEm && !inEx) continue;
      // V8: single brand light (lime) for every trail — logos keep their own colours, the light stays third plane
      const col = '#b7ff3c';
      const pts = [];
      for (let j = 0; j <= 8; j++) { const q = p - j * 0.006; pts.push(this.state(f, q, this.screenPoint(q))); }
      for (let j = 0; j < pts.length - 1; j++) {
        const a = pts[j];
        const b = pts[j + 1];
        g.strokeStyle = col;
        g.globalAlpha = 0.3 * (1 - j / pts.length) * Math.min(1, a.op + 0.2);
        g.lineWidth = (f.kind === 'net' ? this.L * 0.16 : this.L * 0.08) * (1 - j / pts.length) * Math.max(0.4, a.sc);
        g.beginPath();
        g.moveTo(a.x, a.y);
        g.lineTo(b.x, b.y);
        g.stroke();
      }
    }
    // sparks: deterministic per p, denser during activation pulses and the explosion
    const burst = Math.sin(Math.PI * clamp(span(p, 0.79, 0.9)));
    // V8: fewer particles (≈ ×0.45), lime family only — no multicolour firework
    const n = Math.round(4 + 22 * burst + 6 * smooth(span(p, 0.12, 0.3)) * (1 - smooth(span(p, 0.5, 0.6))));
    const rnd = prng(Math.floor(p * 400));
    for (let i = 0; i < n; i++) {
      const a = Math.PI * (1 + rnd());
      const d = (0.2 + rnd() * (0.5 + burst * 1.4)) * this.L * 2.4;
      const x = S.x + Math.cos(a) * d * (this.mode === 'd' ? 1.5 : 1);
      const y = S.y + Math.sin(a) * d;
      const r = 0.8 + rnd() * 2.2;
      g.globalAlpha = (0.25 + rnd() * 0.45) * (0.35 + burst * 0.8);
      g.fillStyle = ['#b7ff3c', '#d4ff8a', '#8fd62a', '#b7ff3c'][i % 4];
      g.beginPath();
      g.arc(x, y, r, 0, Math.PI * 2);
      g.fill();
    }
    g.globalAlpha = 1;
    g.globalCompositeOperation = 'source-over';
  },
};
